import Vue from 'vue';
import Router from 'vue-router';
import {
  verifyAccessToLessonChatHistory,
  verifyTestLessonToken,
  verifyLessonToken,
  verifyAdminTrainerLessonToken,
  verifyAccessToMaterial,
  verifyAccessToTrainerMaterial,
  verifyAdminDevAccess
} from '@/utils/verifyToken';
import logger from '@/modules/Logger';
import { verifyPageURL, verifyTrialLesson } from '@/utils/trialLesson';
import { credentialRouteGuard } from '@/modules/myStageGraphQL/Auth';
import i18n from '@/lang/_i18n';
import store from '@/store/index';

const Lesson = () => import('@/views/Lesson');
const Error = () => import('@/views/Error');
const BrowserNotSupported = () => import('@/views/BrowserNotSupported');
const JwtError = () => import('@/views/JwtError');
const TrialLessonGenerator = () => import('@/views/TrialLessonGenerator');
const MaterialBuilder = () => import('@/views/MaterialBuilder');
const HtmlMaterial = () => import('@/views/HtmlMaterial');
const HtmlMaterialTrainer = () => import('@/views/HtmlMaterialTrainer');
const LessonChatHistory = () => import('@/views/LessonChatHistory');
const RedirectToHome = () => import('@/views/RedirectToHome');

Vue.use(Router);

const router = new Router({
  mode: 'history',
  base: process.env.VUE_APP_BASE_PATH,
  routes: [
    {
      path: '*',
      component: Error
    },
    {
      path: '/error',
      name: 'Error',
      component: Error
    },
    {
      path: '/jwt-error',
      name: 'JwtError',
      component: JwtError
    },
    {
      path: '/redirect-to-home',
      name: 'RedirectToHome',
      component: RedirectToHome,
      beforeEnter: (to, from, next) => {
        logger.info('Force sending of logs', { operation: 'send-batch-log' });
        next();
      }
    },
    {
      path: '/lesson/:lessonId',
      name: 'Lesson',
      component: Lesson,
      beforeEnter: (to, from, next) => verifyLessonToken(to, from, next),
      meta: { requiresUserSetting: true, requiresAuth: true }
    },
    {
      path: '/cms-preview/:lessonNo',
      name: 'CMSPreviewMaterial',
      component: Lesson,
      beforeEnter: (to, from, next) => verifyLessonToken(to, from, next)
    },
    {
      path: '/test-lesson/:lessonId',
      name: 'TestTrainer',
      component: Lesson,
      beforeEnter: (to, from, next) => verifyAdminTrainerLessonToken(to, from, next),
      meta: { requiresUserSetting: true, requiresAuth: true }
    },
    {
      path: '/trial-lesson-generator/:serviceId/:pageUrl',
      name: 'TrialLessonGenerator',
      component: TrialLessonGenerator,
      beforeEnter: (to, from, next) => verifyPageURL(to, from, next)
    },
    {
      path: '/trial-lesson/:serviceId/:lessonId/:pCompanyId/:hashCode/:user',
      name: 'TrialLessonPage',
      component: Lesson,
      beforeEnter: (to, from, next) => verifyTrialLesson(to, from, next)
    },
    {
      path: '/:lessonId/unsupported',
      name: 'BrowserNotSupported',
      component: BrowserNotSupported,
      beforeEnter: (to, from, next) => verifyLessonToken(to, from, next),
      meta: { requiresAuth: true }
    },
    {
      path: '/:lessonId/assess-prod/:isStudent/:partnerId/:webrtcId',
      name: 'Assess',
      component: Lesson,
      beforeEnter: (to, from, next) => verifyTestLessonToken(to, from, next)
    },
    {
      path: '/material-builder/:rankId/:lessonId/:isCreate?',
      name: 'MaterialBuilder',
      component: MaterialBuilder
    },
    {
      path: '/html-material/:userTypeMaterial/:programId/:rankId/:lessonId/:revamp',
      name: 'HtmlMaterial',
      component: HtmlMaterial,
      beforeEnter: (to, from, next) => verifyAccessToMaterial(to, from, next),
      meta: { requiresUserSetting: true, requiresAuth: true }
    },
    {
      path: '/html-material/trainer/:programId/:rankId',
      name: 'TrainerHtmlMaterial',
      component: HtmlMaterialTrainer,
      beforeEnter: (to, from, next) => verifyAccessToTrainerMaterial(to, from, next),
      meta: { requiresAuth: true }
    },
    {
      path: '/history/:lessonId',
      name: 'LessonChatHistory',
      component: LessonChatHistory,
      beforeEnter: (to, from, next) => verifyAccessToLessonChatHistory(to, from, next),
      meta: { requiresUserSetting: true, requiresAuth: true }
    },
    {
      path: '/dev-test/:lessonId',
      name: 'DevTest',
      component: Lesson,
      beforeEnter: (to, from, next) => verifyAdminDevAccess(to, from, next),
      meta: { requiresUserSetting: true, requiresAuth: true }
    }
  ]
});

router.beforeEach(credentialRouteGuard);

router.beforeResolve(async (to, from, next) => {
  if (to.matched.some((record) => record.meta.requiresUserSetting)) {
    try {
      await store.dispatch('userOtherSetting/getUserOtherSetting', { routeName: to.name });
      i18n.locale = store.getters['userOtherSetting/targetLanguage'];
      store.commit('userOtherSetting/setLanguage', i18n.locale);
      logger.info('Setting language of user to: ' + i18n.locale, { operation: 'set:language' });
    } catch (error) {
      console.error('Error loading user setting:', error);
    }
  }

  next();
});
export default router;
